import { useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { useIsPureAdmin } from '@/react/hooks/useUser';
import { usePolicy } from '@/react/portainer/policies/queries/usePolicy';
import { getK8sAppliedPolicies } from '@/react/portainer/policies/utils/policy-types-metadata';
import { PolicyType } from '@/react/portainer/policies/types';
import { useKubernetesSecurityInitialValues } from '@/react/portainer/policies/components/kubernetes/security/useKubernetesSecurityInitialValues';
import { useKubernetesSetupInitialValues } from '@/react/portainer/policies/components/kubernetes/setup/useKubernetesSetupInitialValues';
import { useKubernetesRBACInitialValues } from '@/react/portainer/policies/components/kubernetes/rbac/useKubernetesRBACInitialValues';
import { useKubernetesRegistryInitialValues } from '@/react/portainer/policies/components/kubernetes/registry/useKubernetesRegistryInitialValues';
import { KubernetesSecurityFormFields } from '@/react/portainer/policies/components/kubernetes/security/KubernetesSecurityFormFields';
import { KubernetesSetupFormFields } from '@/react/portainer/policies/components/kubernetes/setup/KubernetesSetupFormFields';
import { KubernetesRBACFormFields } from '@/react/portainer/policies/components/kubernetes/rbac/KubernetesRBACFormFields';
import { KubernetesRegistryAccessDatatable } from '@/react/portainer/policies/components/kubernetes/registry/KubernetesRegistryAccessDatatable';

import { PageHeader } from '@@/PageHeader';
import { Widget, WidgetBody } from '@@/Widget';
import { CollapseExpandButton } from '@@/CollapseExpandButton';
import { Badge, BadgeType } from '@@/Badge';
import { Link } from '@@/Link';

export function PoliciesView() {
  const environmentId = useEnvironmentId();
  const environmentQuery = useEnvironment(environmentId);
  const isPureAdmin = useIsPureAdmin();

  const policies = environmentQuery.data?.Policies;

  const appliedPolicies = getK8sAppliedPolicies(policies);

  return (
    <>
      <PageHeader
        title="Policies"
        breadcrumbs={[{ label: 'Policies' }]}
        reload
      />

      <div className="row">
        <div className="col-sm-12">
          {appliedPolicies.length === 0 ? (
            <Widget>
              <WidgetBody loading={environmentQuery.isLoading}>
                <div className="text-muted">
                  <p className="mb-2">
                    No policies are currently applied to this environment.
                  </p>
                  <p className="mb-2">
                    Policies allow you to enforce security settings, configure
                    cluster resources, manage RBAC, and control registry access
                    across multiple environments.
                  </p>
                  {isPureAdmin ? (
                    <p>
                      To create and manage policies, visit the{' '}
                      <Link
                        to="portainer.policies"
                        data-cy="policies-management-link"
                      >
                        Policy Management
                      </Link>{' '}
                      page.
                    </p>
                  ) : (
                    <p>
                      Contact your Portainer administrator to enable policies
                      for this environment.
                    </p>
                  )}
                </div>
              </WidgetBody>
            </Widget>
          ) : (
            <div className="flex flex-col gap-4">
              {appliedPolicies.map((item) => (
                <PolicySection
                  key={item.key}
                  label={item.label}
                  policyId={item.policy!.policyID!}
                  policyName={item.policy!.policyName}
                  policyType={item.type}
                  status={item.policy!.policyStatus}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function getStatusBadgeType(status: string): BadgeType {
  if (status === 'applied') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'warning') return 'warn';
  return 'muted';
}

interface PolicySectionProps {
  label: string;
  policyId: number;
  policyName?: string;
  policyType: PolicyType;
  status?: {
    Status: string;
    Message: string;
  };
}

function PolicySection({
  label,
  policyId,
  policyName,
  policyType,
  status,
}: PolicySectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const policyQuery = usePolicy(policyId);
  const policy = policyQuery.data;

  return (
    <Widget>
      <div className="widget-header">
        <div className="row">
          <div className="col-sm-12">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="widget-title flex w-full items-center gap-2 border-0 bg-transparent text-left hover:opacity-80"
            >
              <CollapseExpandButton
                isExpanded={isExpanded}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              />
              <span className="flex-1">
                {label} - {policyName || 'Unknown'}
              </span>
              {status?.Status && (
                <Badge type={getStatusBadgeType(status.Status)}>
                  {status.Status}
                </Badge>
              )}
            </button>
          </div>
        </div>
      </div>
      {isExpanded && (
        <WidgetBody loading={policyQuery.isLoading}>
          {status?.Message && (
            <div className="small text-muted mb-4">{status.Message}</div>
          )}
          {policy && (
            <fieldset
              disabled
              className="[&_button]:hidden [&_input]:!bg-gray-3 [&_select]:!bg-gray-3"
            >
              <PolicyFormContent policy={policy} policyType={policyType} />
            </fieldset>
          )}
        </WidgetBody>
      )}
    </Widget>
  );
}

/**
 * Renders the appropriate policy form based on policy type.
 * Each case renders a component that calls only its required hook.
 */
function PolicyFormContent({
  policy,
  policyType,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
  policyType: PolicyType;
}) {
  switch (policyType) {
    case 'security-k8s':
      return <SecurityPolicyContent policy={policy} />;
    case 'setup-k8s':
      return <SetupPolicyContent policy={policy} />;
    case 'rbac-k8s':
      return <RBACPolicyContent policy={policy} />;
    case 'registry-k8s':
      return <RegistryPolicyContent policy={policy} />;
    default:
      return null;
  }
}

function SecurityPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesSecurityInitialValues(policy);
  return (
    <KubernetesSecurityFormFields
      formData={initialValues}
      onChange={() => {}}
    />
  );
}

function SetupPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesSetupInitialValues(policy);
  return (
    <KubernetesSetupFormFields
      formData={initialValues}
      onChange={() => {}}
      timeZone="UTC"
      onChangeTimeZone={() => {}}
    />
  );
}

function RBACPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesRBACInitialValues(policy);
  return (
    <KubernetesRBACFormFields formData={initialValues} onChange={() => {}} />
  );
}

function RegistryPolicyContent({
  policy,
}: {
  policy: NonNullable<ReturnType<typeof usePolicy>['data']>;
}) {
  const initialValues = useKubernetesRegistryInitialValues(policy);
  return (
    <KubernetesRegistryAccessDatatable
      formData={{ accessRows: initialValues.accessRows || [] }}
      onChange={() => {}}
    />
  );
}
